import { Box, Button, Dialog, DialogActions, DialogTitle, FormControl } from '@mui/material'
import { useCreateDictionaryMutation, useEditDictionaryMutation } from '../../store/redux/reducers/dictionarySlice'
import { useForm } from 'react-hook-form'
import { defaultDictionaryFormValues, DictionaryFormValues } from '../../store/types/form/DictionaryFormValues'
import TextInput from '../../components/Form/Inputs/TextInput'
import { t } from 'i18next'
import React from 'react'
import { createErrorMessage, stopPropagationForKey } from '../../store/utils'
import { LoadingButton } from '@mui/lab'
import { useNotification } from '../../store/redux/hooks'
import Dictionary from '../../store/types/models/Dictionary'
import { PopupState } from 'material-ui-popup-state/hooks'
import { BaseDialogProps } from '../../components/Dialog/BaseDialogProps'

type AddDictionaryProps = BaseDialogProps & {
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  editedDictionary?: Dictionary
  actionMenuPopupState?: PopupState
}

const AddEditDictionaryDialog = ({ open, setOpen, editedDictionary, actionMenuPopupState }: AddDictionaryProps) => {
  const { displayNotification } = useNotification()
  const [addDictionary] = useCreateDictionaryMutation()
  const [editDictionary] = useEditDictionaryMutation()

  const {
    handleSubmit,
    control,
    reset,
    formState: { isSubmitting },
  } = useForm<DictionaryFormValues>({
    defaultValues: editedDictionary ? editedDictionary : defaultDictionaryFormValues,
  })

  const submitClick = () => {
    editedDictionary ? handleSubmit(handleSubmitEdit)() : handleSubmit(handleSubmitCreate)()
  }

  const handleSubmitCreate = async (data: DictionaryFormValues) => {
    try {
      await addDictionary(data).unwrap()
      displayNotification({ message: t('page.dictionarySettings.alert.dictionaryCreated'), type: 'success' })
      reset()
      setOpen(false)
    } catch (error) {
      let message = `${t('page.dictionarySettings.alert.dictionaryCreatedError')}: ${createErrorMessage(error)}`

      displayNotification({
        message: message,
        type: 'error',
      })
      reset(data)
    }
  }
  const handleSubmitEdit = async (data: DictionaryFormValues) => {
    if (!editedDictionary) {
      return
    }
    try {
      await editDictionary({ id: editedDictionary.id, dictionary: data }).unwrap()
      displayNotification({ message: t('page.dictionarySettings.alert.dictionaryEdited'), type: 'success' })
      reset(data)
      setOpen(false)
      actionMenuPopupState?.close()
    } catch (error) {
      let message = `${t('page.dictionarySettings.alert.dictionaryEditError')}: ${createErrorMessage(error)}`

      displayNotification({
        message: message,
        type: 'error',
      })
      reset(data)
    }
  }

  const handleClose = () => {
    reset(editedDictionary ? editedDictionary : defaultDictionaryFormValues)
    setOpen(false)
  }

  return (
    <Dialog
      open={open}
      onClose={() => handleClose()}
      PaperProps={{
        sx: {
          px: 2,
          py: 1,
          minWidth: 350,
        },
      }}
    >
      <DialogTitle sx={{ px: 1, py: 1 }}>
        {editedDictionary
          ? t('page.dictionarySettings.dialog.addEdit.editTitle')
          : t('page.dictionarySettings.dialog.addEdit.addTitle')}
      </DialogTitle>
      <FormControl
        onSubmit={submitClick}
        onKeyDown={e => stopPropagationForKey(e, 'Tab')}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 2,
        }}
      >
        <Box
          sx={{
            maxHeight: '75vh',
            overflow: 'auto',
            p: 1,
            gap: 2,
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            alignItems: 'center',
          }}
        >
          <TextInput
            name={'group'}
            control={control}
            label={t('page.dictionarySettings.addDictionary.group')}
            required
            rules={{ required: t('page.dictionarySettings.addDictionary.groupRequired') }}
            maxLength={255}
          />
          <TextInput
            name={'value'}
            control={control}
            label={t('page.dictionarySettings.addDictionary.value')}
            required
            rules={{ required: t('page.dictionarySettings.addDictionary.valueRequired') }}
            maxLength={255}
            multiline
          />
          <TextInput
            name={'key'}
            control={control}
            label={t('page.dictionarySettings.addDictionary.key')}
            maxLength={100}
          />
        </Box>
      </FormControl>
      <DialogActions>
        <Button onClick={handleClose} size="small">
          {t('page.schedule.confirmCopyDialog.return')}
        </Button>
        <LoadingButton
          size="small"
          variant="contained"
          type="submit"
          fullWidth
          onClick={() => submitClick()}
          loading={isSubmitting}
          disabled={isSubmitting}
        >
          {t('page.dictionarySettings.buttons.saveDictionary')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

export default AddEditDictionaryDialog